import { Link, useLoaderData } from 'react-router-dom';
import { useMemo } from 'react';
import { Container } from '@dma/ui';
import type { SeoMeta } from '@dma/types';
import type { ScientificEntryDto } from '../application/dto/ScientificEntryDto';
import { useDocumentHead } from '../../../app/seo/useDocumentHead';
import { ScientificMarkdown } from './ScientificMarkdown';
import './ScientificEntryDetailPage.css';

export interface ScientificEntryDetailLoaderData {
  heading: string;
  basePath: string;
  entry: ScientificEntryDto;
}

type PhysicalPropertyKey = keyof ScientificEntryDto['physicalProperties'];

const PROPERTY_LABELS: Record<PhysicalPropertyKey, string> = {
  hardness: 'سختی (مقیاس موس)',
  color: 'رنگ',
  luster: 'جلا',
  streak: 'رنگ خط',
  crystalSystem: 'سیستم بلوری',
  cleavage: 'رخ',
  fracture: 'شکست',
  specificGravity: 'وزن مخصوص'
};

const PROPERTY_ORDER = Object.keys(PROPERTY_LABELS) as PhysicalPropertyKey[];
const SEO_DESCRIPTION_MAX_LENGTH = 160;

function buildEntrySeo(entry: ScientificEntryDto): SeoMeta {
  const description =
    entry.description.length > SEO_DESCRIPTION_MAX_LENGTH
      ? `${entry.description.slice(0, SEO_DESCRIPTION_MAX_LENGTH).trimEnd()}…`
      : entry.description || entry.classification;

  return {
    title: entry.title,
    description,
    ogImage: entry.images[0]?.url
  };
}

export function ScientificEntryDetailPage() {
  const { heading, basePath, entry } = useLoaderData() as ScientificEntryDetailLoaderData;
  // Derived here, not added to the ScientificEntry domain entity — this
  // Sprint's scope explicitly preserves that model as-is (mineralogy/
  // petrology, draft/published, slug, images, references).
  const seo = useMemo(() => buildEntrySeo(entry), [entry]);
  useDocumentHead(seo);

  const properties = PROPERTY_ORDER.map((key) => ({
    key,
    label: PROPERTY_LABELS[key],
    value: entry.physicalProperties[key]
  })).filter((item) => Boolean(item.value));

  const galleryImages = entry.images.slice(1);

  return (
    <Container>
      <article className="dma-scientific-detail">
        <Link to={basePath} className="dma-scientific-detail__back">
          ← بازگشت به {heading}
        </Link>

        {entry.images[0] ? (
          <img
            className="dma-scientific-detail__image"
            src={entry.images[0].url}
            alt={entry.images[0].caption || entry.title}
            loading="lazy"
          />
        ) : null}

        <h1>{entry.title}</h1>
        <p className="dma-scientific-detail__classification">{entry.classification}</p>
        {entry.chemicalFormula ? (
          <p className="dma-scientific-detail__formula">فرمول شیمیایی: {entry.chemicalFormula}</p>
        ) : null}

        {entry.description ? <ScientificMarkdown content={entry.description} /> : null}

        {properties.length > 0 ? (
          <section className="dma-scientific-detail__section">
            <h2>خواص فیزیکی و شیمیایی</h2>
            <dl className="dma-scientific-detail__properties">
              {properties.map((item) => (
                <div key={item.key}>
                  <dt>{item.label}</dt>
                  <dd>{item.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        {galleryImages.length > 0 ? (
          <section className="dma-scientific-detail__section">
            <h2>تصاویر</h2>
            <div className="dma-scientific-detail__gallery">
              {galleryImages.map((image) => (
                <figure key={image.url}>
                  <img src={image.url} alt={image.caption || entry.title} loading="lazy" />
                  {image.caption ? <figcaption>{image.caption}</figcaption> : null}
                </figure>
              ))}
            </div>
          </section>
        ) : null}

        {entry.occurrenceLocations.length > 0 ? (
          <section className="dma-scientific-detail__section">
            <h2>محل‌های یافت شدن</h2>
            <ul>
              {entry.occurrenceLocations.map((location) => (
                <li key={location}>{location}</li>
              ))}
            </ul>
          </section>
        ) : null}

        {entry.references.length > 0 ? (
          <section className="dma-scientific-detail__section">
            <h2>منابع</h2>
            <ul className="dma-scientific-detail__references">
              {entry.references.map((reference) => (
                <li key={reference.title}>
                  {reference.url ? (
                    <a href={reference.url} target="_blank" rel="noreferrer">
                      {reference.title}
                    </a>
                  ) : (
                    reference.title
                  )}
                  {reference.author ? <span> — {reference.author}</span> : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </article>
    </Container>
  );
}
